/**
 * Section Drag and Drop Manager
 * 
 * Handles drag-and-drop reordering of sections and saves preferences
 * to both database (if logged in) and localStorage (as fallback)
 */

import { settings } from './Settings.js';

export class SectionDragDrop {
    constructor(containerSelector, sectionSelector, apiBase = '/api/user/', isPanelLevel = false) {
        this.container = document.querySelector(containerSelector);
        this.sectionSelector = sectionSelector;
        this.apiBase = apiBase;
        this.draggedElement = null;
        this.dragOverElement = null;
        this.preferencesKey = isPanelLevel ? 'panelOrder' : 'sectionOrder';
        this.isPanelLevel = isPanelLevel;
        this.isLoggedIn = false;
        this.userId = null;
        
        // Drag and drop enabled state (default to false, load from settings)
        this.enabled = settings.get('dragDropEnabled') || false;
        
        // Store event handlers for cleanup
        this.sectionHandlers = new Map();
        
        this.init();
    }
    
    async init() {
        if (!this.container) {
            console.warn('SectionDragDrop: Container not found:', this.containerSelector);
            return;
        }
        
        // Check if user is logged in
        await this.checkLoginStatus();
        
        // Load saved section order
        await this.loadSectionOrder();
        
        // Setup drag and drop (only if enabled)
        if (this.enabled) {
            this.setupDragAndDrop();
        }
    }
    
    /**
     * Check if user is logged in
     */
    async checkLoginStatus() {
        try {
            // Try to get user info from session/auth check
            const response = await fetch(`${this.apiBase.replace('/user/', '/auth/')}check.php`);
            if (response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json();
                    this.isLoggedIn = data.logged_in || false;
                    this.userId = data.user_id || null;
                } else {
                    // Response is not JSON (likely HTML 404 page) - old PHP endpoint doesn't exist
                    this.isLoggedIn = false;
                }
            }
        } catch (e) {
            // Silently fail - auth check is optional for planner mode
            this.isLoggedIn = false;
        }
    }
    
    /**
     * Load saved section order from database or localStorage
     */
    async loadSectionOrder() {
        let savedOrder = null;
        
        // Try to load from database first (if logged in)
        if (this.isLoggedIn) {
            try {
                const response = await fetch(`${this.apiBase}preferences.php`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.preferences) {
                        const key = this.isPanelLevel ? 'panelOrder' : 'sectionOrder';
                        if (data.preferences[key]) {
                            savedOrder = data.preferences[key];
                            // Sync to localStorage for offline access
                            settings.set(this.preferencesKey, savedOrder);
                        }
                    }
                }
            } catch (e) {
                console.log('Failed to load preferences from database:', e);
            }
        }
        
        // Fallback to localStorage
        if (!savedOrder) {
            const stored = settings.get(this.preferencesKey);
            if (stored && Array.isArray(stored)) {
                savedOrder = stored;
            }
        }
        
        // Apply saved order if available
        if (savedOrder && savedOrder.length > 0) {
            this.applySectionOrder(savedOrder);
        }
    }
    
    /**
     * Apply saved section order
     */
    applySectionOrder(order) {
        const sections = Array.from(this.container.querySelectorAll(this.sectionSelector));
        const sectionMap = new Map();
        
        // Create a map of section IDs to elements
        sections.forEach(section => {
            let sectionId;
            if (this.isPanelLevel && section.dataset.panelId) {
                sectionId = section.dataset.panelId;
            } else {
                sectionId = section.dataset.sectionId || this.getSectionId(section);
            }
            if (sectionId) {
                sectionMap.set(sectionId, section);
            }
        });
        
        // Reorder sections based on saved order
        const orderedSections = [];
        order.forEach(sectionId => {
            const section = sectionMap.get(sectionId);
            if (section) {
                orderedSections.push(section);
                sectionMap.delete(sectionId);
            }
        });
        
        // Add any remaining sections that weren't in the saved order
        sectionMap.forEach(section => {
            orderedSections.push(section);
        });
        
        // Reorder in DOM
        orderedSections.forEach(section => {
            this.container.appendChild(section);
        });
    }
    
    /**
     * Get section ID from element
     */
    getSectionId(section) {
        // For panel-level, use panel-id
        if (this.isPanelLevel && section.dataset.panelId) {
            return section.dataset.panelId;
        }
        
        // Try to get from data attribute
        if (section.dataset.sectionId) {
            return section.dataset.sectionId;
        }
        
        // Generate ID from section content (h3 or h4 text)
        const header = section.querySelector('h3, h4');
        if (header) {
            return header.textContent.trim().toLowerCase().replace(/\s+/g, '-');
        }
        
        // Fallback to index
        return `section-${Array.from(this.container.querySelectorAll(this.sectionSelector)).indexOf(section)}`;
    }
    
    /**
     * Setup drag and drop functionality
     */
    setupDragAndDrop() {
        const sections = this.container.querySelectorAll(this.sectionSelector);
        
        sections.forEach(section => {
            // Skip if not enabled
            if (!this.enabled) {
                return;
            }
            
            // Ensure section has an ID
            if (!section.dataset.sectionId) {
                section.dataset.sectionId = this.getSectionId(section);
            }
            
            // Make section draggable
            section.draggable = true;
            section.classList.add('draggable-section');
            
            // Add drag handle (optional - can be styled as a grip icon)
            // Skip for panel-level dragging (panels have their own styling)
            if (!this.isPanelLevel && !section.querySelector('.drag-handle')) {
                const dragHandle = document.createElement('div');
                dragHandle.className = 'drag-handle';
                dragHandle.innerHTML = '⋮⋮';
                dragHandle.title = 'Drag to reorder';
                dragHandle.style.cssText = 'cursor: move; color: var(--text-secondary, #999); font-size: 14px; padding: 4px; user-select: none;';
                section.insertBefore(dragHandle, section.firstChild);
            }
            
            // Create event handlers
            const dragStartHandler = (e) => {
                if (!this.enabled) {
                    e.preventDefault();
                    return;
                }
                // For panel-level: only allow panel drag from empty space or drag handle area
                if (this.isPanelLevel) {
                    // Check if the drag originated from a child element (section, button, input, etc.)
                    const target = e.target;
                    
                    // Check if target is a collapsible section or inside one
                    const isSection = target.classList.contains('collapsible-section') ||
                                     target.closest('.collapsible-section');
                    
                    // Check if target is an interactive element
                    const isInteractive = target.tagName === 'BUTTON' ||
                                         target.tagName === 'INPUT' ||
                                         target.tagName === 'SELECT' ||
                                         target.closest('button') ||
                                         target.closest('input') ||
                                         target.closest('select') ||
                                         target.closest('.drag-handle');
                    
                    // Allow panel drag only if:
                    // 1. Target is the panel itself (not a child section or interactive element)
                    // 2. OR target is in the top-left corner area (where the drag handle appears)
                    const rect = section.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    const isInDragHandleArea = x < 60 && y < 60; // Top-left 60x60px area
                    
                    // If dragging from a section or interactive element (and not in drag handle area), prevent panel drag
                    if ((isSection || isInteractive) && !isInDragHandleArea) {
                        // Prevent panel drag - let the section drag handle it
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }
                
                this.draggedElement = section;
                section.classList.add('dragging');
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/html', section.outerHTML);
                e.dataTransfer.setData('text/plain', section.dataset.sectionId || section.dataset.panelId || '');
                
                // Mark as panel drag (for panel-level) to distinguish from section drags
                if (this.isPanelLevel) {
                    e.dataTransfer.setData('is-panel-drag', 'true');
                    e.dataTransfer.setData('panel-id', section.dataset.panelId || '');
                }
                
                // Store element reference for trash
                e.dataTransfer.setData('dragged-element-id', section.dataset.sectionId || section.dataset.panelId || section.id || '');
            };
            
            const dragEndHandler = () => {
                section.classList.remove('dragging');
                const dragOver = this.container.querySelector('.drag-over');
                if (dragOver) {
                    dragOver.classList.remove('drag-over');
                }
                this.draggedElement = null;
                this.dragOverElement = null;
            };
            
            const dragOverHandler = (e) => {
                if (!this.enabled) {
                    return;
                }
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                
                if (section !== this.draggedElement) {
                    const dragOver = this.container.querySelector('.drag-over');
                    if (dragOver && dragOver !== section) {
                        dragOver.classList.remove('drag-over');
                    }
                    section.classList.add('drag-over');
                    this.dragOverElement = section;
                }
            };
            
            const dragLeaveHandler = () => {
                if (!this.enabled) {
                    return;
                }
                section.classList.remove('drag-over');
            };
            
            const dropHandler = (e) => {
                if (!this.enabled) {
                    return;
                }
                e.preventDefault();
                section.classList.remove('drag-over');
                
                if (this.draggedElement && this.draggedElement !== section) {
                    // Get all sections
                    const allSections = Array.from(this.container.querySelectorAll(this.sectionSelector));
                    const draggedIndex = allSections.indexOf(this.draggedElement);
                    const targetIndex = allSections.indexOf(section);
                    
                    // Move the dragged element
                    if (draggedIndex < targetIndex) {
                        this.container.insertBefore(this.draggedElement, section.nextSibling);
                    } else {
                        this.container.insertBefore(this.draggedElement, section);
                    }
                    
                    // Save new order
                    this.saveSectionOrder();
                }
            };
            
            // Store handlers for cleanup
            this.sectionHandlers.set(section, {
                dragstart: dragStartHandler,
                dragend: dragEndHandler,
                dragover: dragOverHandler,
                dragleave: dragLeaveHandler,
                drop: dropHandler
            });
            
            // Add event listeners
            section.addEventListener('dragstart', dragStartHandler);
            section.addEventListener('dragend', dragEndHandler);
            section.addEventListener('dragover', dragOverHandler);
            section.addEventListener('dragleave', dragLeaveHandler);
            section.addEventListener('drop', dropHandler);
        });
    }
    
    /**
     * Get current section order
     */
    getCurrentSectionOrder() {
        const sections = Array.from(this.container.querySelectorAll(this.sectionSelector));
        return sections.map(section => {
            if (this.isPanelLevel && section.dataset.panelId) {
                return section.dataset.panelId;
            }
            return section.dataset.sectionId || this.getSectionId(section);
        });
    }
    
    /**
     * Save section order to database and localStorage
     */
    async saveSectionOrder() {
        const order = this.getCurrentSectionOrder();
        
        // Save to localStorage immediately (fast, always works)
        settings.set(this.preferencesKey, order);
        
        // Save to database if logged in (async, don't wait)
        if (this.isLoggedIn) {
            try {
                // Load existing preferences first to merge
                let existingPrefs = {};
                try {
                    const getResponse = await fetch(`${this.apiBase}preferences.php`);
                    if (getResponse.ok) {
                        const getData = await getResponse.json();
                        if (getData.success && getData.preferences) {
                            existingPrefs = getData.preferences;
                        }
                    }
                } catch (e) {
                    // Ignore errors loading existing prefs
                }
                
                // Merge with new order
                const key = this.isPanelLevel ? 'panelOrder' : 'sectionOrder';
                const preferences = {
                    ...existingPrefs,
                    [key]: order
                };
                
                const response = await fetch(`${this.apiBase}preferences.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ preferences })
                });
                
                if (!response.ok) {
                    console.warn('Failed to save preferences to database');
                }
            } catch (e) {
                console.warn('Error saving preferences to database:', e);
            }
        }
    }
    
    /**
     * Reset to default order
     */
    resetOrder() {
        settings.set(this.preferencesKey, []);
        location.reload(); // Reload to apply default order
    }
    
    /**
     * Enable drag and drop
     */
    enable() {
        if (this.enabled) {
            return;
        }
        
        this.enabled = true;
        settings.set('dragDropEnabled', true);
        
        // Setup drag and drop
        this.setupDragAndDrop();
    }
    
    /**
     * Disable drag and drop
     */
    disable() {
        if (!this.enabled) {
            return;
        }
        
        this.enabled = false;
        settings.set('dragDropEnabled', false);
        
        // Remove drag handles and make sections non-draggable
        const sections = this.container.querySelectorAll(this.sectionSelector);
        sections.forEach(section => {
            section.draggable = false;
            section.classList.remove('draggable-section');
            
            // Remove drag handle
            const dragHandle = section.querySelector('.drag-handle');
            if (dragHandle) {
                dragHandle.remove();
            }
            
            // Remove event listeners
            const handlers = this.sectionHandlers.get(section);
            if (handlers) {
                section.removeEventListener('dragstart', handlers.dragstart);
                section.removeEventListener('dragend', handlers.dragend);
                section.removeEventListener('dragover', handlers.dragover);
                section.removeEventListener('dragleave', handlers.dragleave);
                section.removeEventListener('drop', handlers.drop);
                this.sectionHandlers.delete(section);
            }
        });
        
        // Clear drag-over states
        this.container.querySelectorAll('.drag-over').forEach(el => {
            el.classList.remove('drag-over');
        });
        
        this.draggedElement = null;
        this.dragOverElement = null;
    }
    
    /**
     * Toggle drag and drop enabled state
     */
    toggle() {
        if (this.enabled) {
            this.disable();
        } else {
            this.enable();
        }
        return this.enabled;
    }
    
    /**
     * Check if drag and drop is enabled
     */
    isEnabled() {
        return this.enabled;
    }
}

